/**
 * 상담 기록 모델
 * Counseling Record Model
 */

export interface CounselingRecord {
  // 기본 정보
  id: string;
  student_id: string;
  counselor_id: string;
  counselor_name: string;
  organization_id: string;

  // 상담 정보
  counseling_date: string; // YYYY-MM-DD
  counseling_type: CounselingType;
  duration_minutes?: number;

  // 상담 내용
  topic: string;
  content: string;
  student_feedback?: string;

  // 평가 (1-5 점)
  growth_score?: number;
  attitude_score?: number;
  concentration_score?: number;
  goal_achievement_score?: number;

  // 목표 및 계획
  current_issues?: string;
  action_plan?: string;
  next_goal?: string;
  next_counseling_date?: string;

  // AI 분석
  ai_summary?: string;
  ai_recommendation?: string;

  // 학습 데이터 연동 (최근 7일)
  related_daily_records?: StudentDailyRecord[];
  related_focus_metrics?: FocusMetric[];
  related_study_goals?: StudyGoal[];

  // 상담 효과 측정
  pre_consultation_metrics?: PreConsultationMetrics;
  post_consultation_metrics?: PostConsultationMetrics;
  effectiveness_score?: number;
  evaluation_date?: string;

  // AI 인사이트 (추후 사용)
  ai_insights?: Record<string, any>;
  ai_suggested_topics?: string[];
  ai_risk_flags?: string[];
  ai_generated_at?: string;

  // 공개 설정
  visible_to_student: boolean;
  visible_to_parent: boolean;

  // 메타 정보
  created_at: string;
  updated_at: string;
}

// 학습 데이터 타입 정의
export interface StudentDailyRecord {
  id: string;
  record_date: string;
  emotion?: string;
  fatigue_level?: number;
  sleep_hours?: number;
  note?: string;
  daily_goal_hours?: number;
}

export interface FocusMetric {
  id: string;
  metric_date: string;
  total_study_minutes: number;
  actual_present_minutes: number;
  away_minutes: number;
  focus_score?: number;
  entry_count: number;
  exit_count: number;
}

export interface StudyGoal {
  id: string;
  goal_setter: string; // 'student' | 'staff' | 'parent'
  goal_period: string; // 'daily' | 'weekly' | 'monthly'
  start_date: string;
  end_date: string;
  daily_study_hours?: number;
  weekly_attendance_days?: number;
  achievement_rate?: number;
  is_achieved: boolean;
  notes?: string;
}

export interface PreConsultationMetrics {
  period: {
    start_date: string;
    end_date: string;
  };
  avg_study_hours_per_day: number;
  avg_focus_rate: number;
  avg_goal_achievement_rate: number;
  avg_fatigue_level: number;
  total_study_days: number;
  calculated_at: string;
}

export interface PostConsultationMetrics extends PreConsultationMetrics {
  // 상담 2주 후 측정되는 동일한 지표
}

export interface CounselingRecordCreate {
  student_id: string;
  counseling_date: string;
  counseling_type?: CounselingType;
  duration_minutes?: number;
  topic: string;
  content: string;
  student_feedback?: string;
  growth_score?: number;
  attitude_score?: number;
  concentration_score?: number;
  goal_achievement_score?: number;
  current_issues?: string;
  action_plan?: string;
  next_goal?: string;
  next_counseling_date?: string;
  visible_to_student?: boolean;
  visible_to_parent?: boolean;
}

export interface CounselingRecordUpdate {
  counseling_date?: string;
  counseling_type?: CounselingType;
  duration_minutes?: number;
  topic?: string;
  content?: string;
  student_feedback?: string;
  growth_score?: number;
  attitude_score?: number;
  concentration_score?: number;
  goal_achievement_score?: number;
  current_issues?: string;
  action_plan?: string;
  next_goal?: string;
  next_counseling_date?: string;
  ai_summary?: string;
  ai_recommendation?: string;
  visible_to_student?: boolean;
  visible_to_parent?: boolean;
}

export type CounselingType =
  | 'regular'
  | 'emergency'
  | 'parent_request'
  | 'progress_check'
  | 'goal_setting';

export const CounselingTypeLabels: Record<CounselingType, string> = {
  regular: '정기 상담',
  emergency: '긴급 상담',
  parent_request: '학부모 요청',
  progress_check: '진도 체크',
  goal_setting: '목표 설정',
};

export interface CounselingSummary {
  total_count: number;
  recent_count: number;
  latest_record?: CounselingRecord;
  average_scores: {
    growth: number;
    attitude: number;
    concentration: number;
    goal_achievement: number;
  };
  last_counseling_date?: string;
  next_counseling_date?: string;
}

/**
 * 상담 유형별 색상 반환
 */
export function getCounselingTypeColor(type: CounselingType): string {
  const colors: Record<CounselingType, string> = {
    regular: '#4caf50',
    emergency: '#f44336',
    parent_request: '#ff9800',
    progress_check: '#2196f3',
    goal_setting: '#9c27b0',
  };
  return colors[type];
}

/**
 * 평가 점수 색상 반환
 */
export function getScoreColor(score: number): string {
  if (score >= 4) return '#4caf50'; // 녹색
  if (score >= 3) return '#ff9800'; // 주황색
  return '#f44336'; // 빨간색
}

/**
 * 평가 점수 레이블 반환
 */
export function getScoreLabel(score: number): string {
  if (score === 5) return '매우 우수';
  if (score === 4) return '우수';
  if (score === 3) return '보통';
  if (score === 2) return '미흡';
  return '매우 미흡';
}

/**
 * 날짜 포맷 변환 (YYYY-MM-DD → YYYY년 MM월 DD일)
 */
export function formatCounselingDate(dateStr: string): string {
  const date = new Date(dateStr);

  // 한국 시간으로 변환 (UTC+9)
  const kstOffset = 9 * 60; // 9시간 = 540분
  const kstDate = new Date(date.getTime() + kstOffset * 60 * 1000);

  return `${kstDate.getFullYear()}년 ${kstDate.getMonth() + 1}월 ${kstDate.getDate()}일`;
}

/**
 * 상담 기록 유효성 검사
 */
export function validateCounselingRecord(
  data: CounselingRecordCreate
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.student_id) {
    errors.push('학생을 선택해주세요.');
  }

  if (!data.counseling_date) {
    errors.push('상담 일자를 입력해주세요.');
  }

  if (!data.topic || data.topic.trim().length === 0) {
    errors.push('상담 주제를 입력해주세요.');
  }

  if (!data.content || data.content.trim().length < 10) {
    errors.push('상담 내용을 최소 10자 이상 입력해주세요.');
  }

  // 평가 점수 유효성 검사 (입력된 경우만)
  const scores = [
    data.growth_score,
    data.attitude_score,
    data.concentration_score,
    data.goal_achievement_score,
  ];

  scores.forEach((score, index) => {
    if (score !== undefined && (score < 1 || score > 5)) {
      const labels = ['성장', '태도', '집중력', '목표달성'];
      errors.push(`${labels[index]} 평가 점수는 1-5 사이여야 합니다.`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}
